import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
  import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Download,
    FileText,
    Home,
    X,
  } from "lucide-vue-next";
  import {
    getMasterplanLotStatusLabel,
    getMasterplanStatusBadgeStyle,
  } from "@/v2/helpers/masterplanLotStatus";
  import { formatDate } from "@/helpers/date";
  import { getDocumentName } from "@/helpers/documents";
  import { formatPrice } from "@/helpers/transformation/price";

  type ActiveTab = "details" | "documents" | "packages";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandSubdivisionLotDetailModal',
  props: {
    isOpen: { type: Boolean, required: true },
    lot: { type: Object, required: true },
    stage: { type: Object, default: null },
    lotPreviewUrl: { type: String, default: "" },
  },
  emits: ["close", "enquiry"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;
  const activeTab = ref<ActiveTab>("details");
  const selectedPackageIndex = ref(0);
  const showEnquiryForm = ref(false);
  const previousBodyOverflow = ref("");

  const enquiryForm = reactive({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const enquiryErrors = reactive({ fullName: "", email: "", phone: "" });

  const lotData = computed(() => props.lot?.lotId ?? {});
  const lotDocuments = computed(() => lotData.value.documents ?? []);
  const hasDocuments = computed(() => lotDocuments.value.length > 0);
  const documentCountLabel = computed(() => {
    const count = lotDocuments.value.length;

    return `${count} ${count === 1 ? "document" : "documents"}`;
  });

  const packages = computed(() => props.lot?.landSubdivisionPackages ?? []);
  const hasPackages = computed(() => packages.value.length > 0);
  const currentPackage = computed(
    () => packages.value[selectedPackageIndex.value] ?? null
  );
  const currentPackageImage = computed(
    () => currentPackage.value?.heroImageUrl ?? ""
  );
  const packagePrice = computed(() =>
    currentPackage.value?.packagePrice
      ? formatPrice(currentPackage.value.packagePrice)
      : "--"
  );
  const packageAvailabilityLabel = computed(() => {
    const count = packages.value.length;

    return `${count} Home & Land ${
      count === 1 ? "Package" : "Packages"
    } available for this lot`;
  });
  const stageTitle = computed(() =>
    props.stage?.title ? `Stage ${props.stage.title}` : "--"
  );
  const lotStatusLabel = computed(() =>
    getMasterplanLotStatusLabel(lotData.value.status)
  );
  const registrationDate = computed(() =>
    lotData.value.registrationDate
      ? formatDate(lotData.value.registrationDate, "MMM YYYY")
      : "--"
  );
  const landPrice = computed(() => {
    if (lotData.value.hidePrice || !lotData.value.price) return "--";

    return formatPrice(lotData.value.price);
  });

  const lotPreview = computed(() => props.lotPreviewUrl || "");

  function resetEnquiryForm() {
    enquiryForm.fullName = "";
    enquiryForm.email = "";
    enquiryForm.phone = "";
    enquiryForm.message = "";
    enquiryErrors.fullName = "";
    enquiryErrors.email = "";
    enquiryErrors.phone = "";
  }

  function validateEnquiry() {
    enquiryErrors.fullName = enquiryForm.fullName.trim()
      ? ""
      : "Full name is required";
    enquiryErrors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enquiryForm.email)
      ? ""
      : "Valid email is required";
    enquiryErrors.phone = enquiryForm.phone.trim() ? "" : "Phone is required";
    return !(
      enquiryErrors.fullName ||
      enquiryErrors.email ||
      enquiryErrors.phone
    );
  }

  function submitEnquiry() {
    if (!validateEnquiry()) return;

    emit("enquiry", props.lot, { ...enquiryForm });
    showEnquiryForm.value = false;
    resetEnquiryForm();
  }

  function openDocument(link: string) {
    window.open(link, "_blank", "noopener,noreferrer");
  }

  function downloadDocument(document: { link: string; fileName: string }) {
    const anchor = window.document.createElement("a");

    anchor.href = document.link;
    anchor.download = getDocumentName(document.fileName);
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  }

  function downloadBrochure() {
    const brochureUrl = currentPackage.value?.brochureUrl;

    if (!brochureUrl) return;

    const anchor = window.document.createElement("a");

    anchor.href = brochureUrl;
    anchor.download =
      currentPackage.value?.brochureFileName ||
      `${currentPackage.value?.designName || "home-and-land"}-brochure.pdf`;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.code === "Escape" && props.isOpen) emit("close");
  }

  watch(
    () => props.lot,
    () => {
      activeTab.value = "details";
      selectedPackageIndex.value = 0;
      showEnquiryForm.value = false;
      resetEnquiryForm();
    },
    { immediate: true }
  );

  watch(
    () => props.isOpen,
    (isOpen) => {
      if (isOpen) {
        previousBodyOverflow.value = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = previousBodyOverflow.value;
      }
    },
    { immediate: true }
  );

  document.addEventListener("keydown", onKeydown);
  onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeydown);
    document.body.style.overflow = previousBodyOverflow.value;
  });

const __returned__ = { props, emit, activeTab, selectedPackageIndex, showEnquiryForm, previousBodyOverflow, enquiryForm, enquiryErrors, lotData, lotDocuments, hasDocuments, documentCountLabel, packages, hasPackages, currentPackage, currentPackageImage, packagePrice, packageAvailabilityLabel, stageTitle, lotStatusLabel, registrationDate, landPrice, lotPreview, resetEnquiryForm, validateEnquiry, submitEnquiry, openDocument, downloadDocument, downloadBrochure, onKeydown, computed, onBeforeUnmount, reactive, ref, watch, get ArrowLeft() { return ArrowLeft }, get ChevronLeft() { return ChevronLeft }, get ChevronRight() { return ChevronRight }, get Download() { return Download }, get FileText() { return FileText }, get Home() { return Home }, get X() { return X }, get getMasterplanLotStatusLabel() { return getMasterplanLotStatusLabel }, get getMasterplanStatusBadgeStyle() { return getMasterplanStatusBadgeStyle }, get formatDate() { return formatDate }, get getDocumentName() { return getDocumentName }, get formatPrice() { return formatPrice } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})